import React, { useEffect, useState } from 'react';
import { Box, VStack, HStack, Text, Input, Heading, Image, Spinner, Icon } from '@chakra-ui/react';
import { FiCopy, FiEye, FiEyeOff } from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useAlert } from '../../hooks/useAlert';
import { useLoginMutation, usePasswordChangeMutation } from '../../api';
import Button from '../../components/base/Button';
import { RememberMeCheckbox } from '../../components/base/RememberMeCheckbox';

const RecoveryPage: React.FC = () => {
    const { t, changeLanguage } = useTranslation({ setLangFromURL: true });
    const { success } = useAlert();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const tokenParam = searchParams.get('token');

    const [passwordChange, { isLoading: isPasswordChangeLoading }] = usePasswordChangeMutation();
    const [login, { isLoading: isLoginLoading }] = useLoginMutation();

    const [recoveryData, setRecoveryData] = useState<{
        email: string;
        login: string;
        company_name: string;
        password: string;
    } | null>(null);
    const [recoveryError, setRecoveryError] = useState<string | null>(null);
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (!tokenParam) setRecoveryError(t.login.errors.authError);
    }, []);

    const handleConfirmRecovery = () => {
        if (!tokenParam) return;
        setRecoveryError(null);
        passwordChange({ token: tokenParam })
            .unwrap()
            .then((data) => setRecoveryData(data))
            .catch(() => setRecoveryError(t.login.errors.authError));
    };

    const handleCopyPassword = async () => {
        if (!recoveryData?.password) return;
        try {
            await navigator.clipboard.writeText(recoveryData.password);
            success(t.pages.support.copied);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!recoveryData) return;
        try {
            const result = await login({
                login: recoveryData.login,
                password: recoveryData.password,
                save_auth: rememberMe,
            }).unwrap();
            if (result) {
                changeLanguage(result.language);
                navigate('/payment');
            }
        } catch {
            setRecoveryError(t.login.errors.authError);
        }
    };

    if (isPasswordChangeLoading) {
        return (
            <Box
                flex={1}
                bg="gray.100"
                display="flex"
                alignItems="center"
                justifyContent="center"
                minH="100vh"
            >
                <VStack gap={6} align="center">
                    <Image src="/favicon-192x192.png" alt="Office Hof" boxSize={16} objectFit="contain" />
                    <Heading size="xl" color="gray.800" style={{ fontFamily: "'Oswald', sans-serif" }}>
                        Office Hof
                    </Heading>
                    <Spinner size="xl" color="yellow.600" />
                    <Text fontSize="lg" color="gray.700" fontWeight="medium">
                        {t.login.loadingData}
                    </Text>
                </VStack>
            </Box>
        );
    }

    return (
        <Box minH="100vh" bg="gray.100" display="flex" alignItems="center" justifyContent="center" p={4}>
            <Box w="full" maxW="md" bg="white" p={8}>
                <VStack gap={8} align="stretch">
                    <HStack justify="center" gap={4}>
                        <Box w={12} h={12} borderRadius="lg" overflow="hidden">
                            <Image
                                src="/favicon-192x192.png"
                                alt="Office Hof"
                                boxSize={12}
                                objectFit="contain"
                            />
                        </Box>
                        <VStack align="start" gap={0}>
                            <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                                Office Hof
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                {t.login.subtitle}
                            </Text>
                        </VStack>
                    </HStack>

                    <Heading size="lg" color="gray.800" textAlign="center">
                        {t.login.form.recoveryTitle}
                    </Heading>

                    {tokenParam && !recoveryData && !recoveryError && (
                        <VStack gap={6} align="stretch">
                            <Text fontSize="md" color="gray.700" textAlign="center">
                                {t.login.form.confirmRecoveryMessage}
                            </Text>
                            <Button
                                buttonType="success"
                                size="lg"
                                w="full"
                                onClick={handleConfirmRecovery}
                                isLoading={isPasswordChangeLoading}
                                loadingText={t.login.form.submitButtonLoading}
                            >
                                {t.login.form.confirmRecoveryButton}
                            </Button>
                        </VStack>
                    )}

                    {recoveryError && !recoveryData && (
                        <VStack gap={6} align="stretch">
                            <Text fontSize="md" color="gray.700" textAlign="center">
                                {t.login.form.recoveryFailedMessage}
                            </Text>
                            <Button
                                buttonType="success"
                                size="lg"
                                w="full"
                                onClick={() => navigate('/login')}
                            >
                                {t.login.form.backToLogin}
                            </Button>
                        </VStack>
                    )}

                    {recoveryData && (
                        <form onSubmit={handleSubmit}>
                            <VStack gap={6} align="stretch">
                                {recoveryError && (
                                    <Box p={2} bg="red.50" borderColor="red.200">
                                        <Text color="red.600" fontSize="sm" textAlign={'center'}>
                                            {recoveryError}
                                        </Text>
                                    </Box>
                                )}
                                <Box>
                                    <Text fontSize="md" fontWeight="bold" color="gray.800" mb={3}>
                                        {t.login.form.loginLabel}
                                    </Text>
                                    <Input
                                        value={recoveryData.login}
                                        readOnly
                                        size="lg"
                                        h="48px"
                                        borderRadius="0"
                                        bg="gray.100"
                                        color="gray.700"
                                        onChange={() => {}}
                                    />
                                </Box>
                                <Box>
                                    <Text fontSize="md" fontWeight="bold" color="gray.800" mb={3}>
                                        {t.login.form.companyLabel}
                                    </Text>
                                    <Input
                                        value={recoveryData.company_name}
                                        readOnly
                                        size="lg"
                                        h="48px"
                                        borderRadius="0"
                                        bg="gray.100"
                                        color="gray.700"
                                        onChange={() => {}}
                                    />
                                </Box>
                                <Box>
                                    <Text fontSize="md" fontWeight="bold" color="gray.800" mb={3}>
                                        {t.login.form.newPasswordLabel}
                                    </Text>
                                    <Box position="relative">
                                        <Input
                                            type={showPassword ? 'text' : 'password'}
                                            value={recoveryData.password}
                                            readOnly
                                            size="lg"
                                            h="48px"
                                            pr="80px"
                                            borderRadius="0"
                                            bg="gray.100"
                                            color="gray.700"
                                            onChange={() => {}}
                                        />
                                        <Box
                                            position="absolute"
                                            right="48px"
                                            top="50%"
                                            transform="translateY(-50%)"
                                            cursor="pointer"
                                            onClick={handleCopyPassword}
                                            color="gray.600"
                                            _hover={{ color: 'gray.800' }}
                                            transition="color 0.2s"
                                            title={t.login.form.copyButton}
                                        >
                                            <Icon as={FiCopy} boxSize={5} />
                                        </Box>
                                        <Box
                                            position="absolute"
                                            right="16px"
                                            top="50%"
                                            transform="translateY(-50%)"
                                            cursor="pointer"
                                            onClick={() => setShowPassword(!showPassword)}
                                            color="gray.600"
                                            _hover={{ color: 'gray.800' }}
                                            transition="color 0.2s"
                                        >
                                            <Icon as={showPassword ? FiEyeOff : FiEye} boxSize={5} />
                                        </Box>
                                    </Box>
                                    <Text mt={2} fontSize="sm" color="orange.600" fontWeight="medium">
                                        {t.login.form.savePasswordWarning}
                                    </Text>
                                </Box>
                                <RememberMeCheckbox
                                    checked={rememberMe}
                                    onCheckedChange={setRememberMe}
                                    label={t.login.form.rememberMe}
                                    tooltipText={t.login.form.rememberMeTooltip}
                                />
                                <Button
                                    type="submit"
                                    buttonType="success"
                                    size="lg"
                                    w="full"
                                    isLoading={isLoginLoading}
                                    loadingText={t.login.form.submitButtonLoading}
                                >
                                    {t.login.form.submitButton}
                                </Button>
                            </VStack>
                        </form>
                    )}
                </VStack>
            </Box>
        </Box>
    );
};

export default RecoveryPage;
